/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from "react";
import PropTypes from "prop-types"; // Import prop-types library

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 20px",
  borderWidth: 2,
  borderRadius: "12px",
  borderColor: "#9c27b0",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#757575",
  outline: "none",
  transition: "all .3s ease-in-out",
  cursor: "pointer",
  minHeight: "180px",
};

const activeStyle = {
  borderColor: "#2196f3",
  backgroundColor: "#e3f2fd",
  transform: "scale(1.02)",
};

const acceptStyle = {
  borderColor: "#4caf50",
  backgroundColor: "#e8f5e9",
};

const rejectStyle = {
  borderColor: "#f44336",
  backgroundColor: "#ffebee",
};

function Attachment({
  acceptedFiles = [],
  getRootProps = (f) => f,
  getInputProps = (f) => f,
  isDragActive = (f) => f,
  isDragAccept = (f) => f,
  isDragReject = (f) => f,
  onDeleteFile,
}) {
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "bmp"].includes(ext)) return "📷";
    if (["pdf"].includes(ext)) return "📄";
    if (["doc", "docx"].includes(ext)) return "📝";
    if (["xls", "xlsx"].includes(ext)) return "📊";
    return "📎";
  };

  const files = acceptedFiles.map((file, index) => (
    <div
      key={file.path}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "12px 16px",
        marginBottom: "8px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      }}
    >
      <span style={{ fontSize: "24px", marginRight: "12px" }}>
        {getFileIcon(file.path)}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: "600", color: "#2c3e50", fontSize: "14px" }}>
          {file.path}
        </div>
        <div style={{ fontSize: "12px", color: "#757575", marginTop: "2px" }}>
          {formatFileSize(file.size)}
        </div>
      </div>
      <span
        style={{
          color: "#4caf50",
          fontWeight: "600",
          fontSize: "18px",
          marginRight: "12px",
        }}
      >
        ✓
      </span>
      {onDeleteFile && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeleteFile(index);
          }}
          style={{
            backgroundColor: "#f44336",
            color: "white",
            border: "none",
            borderRadius: "6px",
            padding: "6px 12px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "600",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#d32f2f";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#f44336";
          }}
        >
          🗑️ Remove
        </button>
      )}
    </div>
  ));

  return (
    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>
            {isDragActive ? "📥" : "📎"}
          </div>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#6a1b9a",
              marginBottom: "8px",
            }}
          >
            {isDragActive
              ? "Drop files here..."
              : "Drag & drop files here, or click to browse"}
          </p>
          <p
            style={{
              fontSize: "13px",
              color: "#9e9e9e",
              margin: 0,
            }}
          >
            Supports: Images, PDF, and Text files
          </p>
        </div>
      </div>

      {files.length ? (
        <div style={{ marginTop: "20px" }}>
          <h4
            style={{
              color: "#6a1b9a",
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "8px" }}>📋</span>
            Attached Files ({files.length})
          </h4>
          <div>{files}</div>
        </div>
      ) : null}
    </div>
  );
}

// Add prop type validation
Attachment.propTypes = {
  acceptedFiles: PropTypes.array,
  getRootProps: PropTypes.func,
  getInputProps: PropTypes.func,
  isDragActive: PropTypes.bool,
  isDragAccept: PropTypes.bool,
  isDragReject: PropTypes.bool,
  onDeleteFile: PropTypes.func,
};

export default Attachment;
